<template>
  <ConfirmDialog
      v-model="isOpen"
      @update:agree="agree"
      @update:cancel="cancel"
      @update:open="$event => (isOpen = $event)"
  >
    <template v-slot:title>{{ $t('Отмена процедуры') }}</template>
    <template v-slot:content>
      <v-card-text
          class="py-0 px-0 mt-3 text-black content-label"
      >{{ $t('Вы действительно хотите отменить процедуру?') }}</v-card-text>
      <v-card-text
          class="py-0 px-0 text-black content-label"
      >{{ $t('Укажите, пожалуйста, причину отмены процедуры.') }}<span class="error-color">*</span>
      </v-card-text>

      <v-messages
          active
          class="error-color mt-2"
          v-if="showErrorMessage && !cancelReasonSelected"
          messages=""
      >
        <template v-slot:message>{{ $t('Это поле обязательно для заполнения') }}</template>
      </v-messages>

      <div>
        <v-form ref="form">
          <div v-for="(cancelReason, index) in cancelReasons">
            <div class="checkbox-field">
              <v-checkbox
                  :key="index"
                  :name="cancelReason.name"
                  :label="cancelReason.name"
                  :value="cancelReason"
                  :true-icon="'mdi-check mdi-18px'"
                  :false-icon="'void-checkbox'"
                  v-model="cancelReasonSelected"
                  class="spec-checkbox"
                  hide-details
                  required
                  :rules="[].concat(validation.required())"
              />
            </div>
          </div>

          <div class="mt-6 mb-2">
            <v-textarea
                v-if="showTextarea"
                :rows="3"
                auto-grow
                variant="outlined"
                :placeholder="textareaLabel"
                v-model="textareaSelected"
                maxlength="255"
                required
                hide-details
                :rules="[].concat(validation.required())"
            />
            <v-messages
                active
                class="error-color mt-2"
                v-if="showErrorTextareaMessage"
                messages=""
            >
              <template v-slot:message>{{ $t('Это поле обязательно для заполнения') }}</template>
            </v-messages>
          </div>
        </v-form>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
  import { watch, Ref, ref, useTemplateRef } from 'vue';
  import api from "@/api";
  import { toast } from '@/lib/toast';
  import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
  import validation from "@/components/validation";

  interface CancelReason {
    id?: number;
    manualInput?: boolean;
    name?: string;
    placeHolder?: string;
  }

  const props = defineProps({
    procedure: { type: Object, required: true },
    open: { type: Boolean, required: true, default: false },
  })

  const emit = defineEmits(['procedureCanceled', 'update:open']);

  const templateForm = useTemplateRef<any>('form')

  const cancelReasonSelected: Ref<CancelReason> = ref(null);
  const textareaSelected = ref(null);
  const textareaLabel = ref('');
  const showTextarea = ref(false);
  const showErrorMessage = ref(false);
  const showErrorTextareaMessage = ref(false);
  const isOpen = ref(false);
  const cancelReasons = ref<Array<CancelReason>>([]);

  /**
   * Получаем список причин отмены.
   */
  async function getVocabCancelReasons() {
    const result = await api.get('/vocab/cancel-reasons');

    cancelReasons.value = result;
  }

  async function agree() {
    const result = await templateForm.value?.validate();
    if (!result.valid) {
      if (!cancelReasonSelected.value?.id) {
        showErrorMessage.value = true;
        toast.error('Укажите, пожалуйста, причину отмены процедуры');
        return;
      }

      if (cancelReasonSelected.value.manualInput && !textareaSelected.value) {
        showErrorTextareaMessage.value = true;
        toast.error('Укажите причину отмены процедуры');
        return;
      }

      return;
    }

    try {
      let formData = new FormData();
      formData.append(
          'data',
          JSON.stringify({
            procedure_id: props.procedure?.id,
            cancel_reason_id: cancelReasonSelected.value.id,
            cancel_reason_message: textareaSelected.value,
          })
      );

      const result = await api.post('/procedures/cancel', formData);

      if (result) {
        toast.info('Отменена');
        emit('procedureCanceled');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      close();
    }
  }

  const cancel = () => {
    close();
  }

  const close = () => {
    emit('update:open', false)

    cancelReasonSelected.value = null;
    textareaSelected.value = null;
    showErrorMessage.value = false;
    showErrorTextareaMessage.value = false;
  }

  watch(
      cancelReasonSelected,
      (cancelReason: CancelReason) => {
        showTextarea.value = !!cancelReason?.manualInput;

        if (cancelReason?.manualInput) {
          textareaLabel.value = cancelReason?.placeHolder;
        }
      },
  );

  watch(() => props.open, (newValue) => {
    isOpen.value = newValue;

    if (newValue) {
      getVocabCancelReasons();
    }
  });

</script>
<style scoped lang="scss">
  .error-color {
    opacity: 1;
    color: rgb(var(--v-theme-error));
    font-weight: normal;
  }

  :deep(.checkbox-field) {
    height: 34px;
  }

  :deep(.mdi-check.mdi-18px) {
    border-radius: 3px;
    border: 1px solid rgb(var(--primary-color));
  }

  .content-label,
  :deep(.spec-checkbox .v-label) {
    color: rgb(var(--black-color))!important;
    font-family: Abrade;
    font-size: 16px!important;
    font-style: normal;
    font-weight: 500;
    line-height: normal!important;
  }

  :deep(.spec-checkbox .v-label) {
    height: 20px;
  }

  :deep(.mdi-check.mdi-18px) {
    border-radius: 3px;
    border: 1px solid rgb(var(--primary-color));
  }

  :deep(.void-checkbox) {
    border-radius: 3px;
    border: 1px solid rgba(var(--primary-color));
    background-color: rgb(var(--white-color));
    opacity: 1;
  }

  :deep(v-textarea textarea) {
    font-family: var(--primary-font-family-name);
    font-size: 16px;
  }
</style>
